/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import { emptyStringToNull, nanToNumber, nullToEmptyArray, nullToEmptyString } from "./helpers";
import * as yup from "yup";

export const CostTypeMask = "COS00000";
export const CostTypeMaxLength = 8;
export const InventoryTypeMask = "0000000";
export const InventoryTypeMaxLength = 7;
export const StandardTemplateIdRegex = /^STD-\w{3}\d{2}\w{1,50}-\d{4}$/;
export const RevisedTemplateIdRegex = /^REV-\w{3}\d{2}\w{1,50}-\d{4}$/;
export const StandardTemplateIdMask = "STD{-}`aaa00a*[************************************************]{-}`0000";
export const RevisedTemplateIdMask = "REV{-}`aaa00a*[************************************************]{-}`0000";
export const MaxDocumentSizeInBytes = 5242880;
export const MaxDocumentSizeInMegabytes = MaxDocumentSizeInBytes / (1024 * 1024);

export enum Department {
  SystemAdministration = "system_administration",
  Accounting = "accounting",
  Administration = "administration",
  Design = "design",
  Logistics = "logistics",
  Operations = "operations",
  Purchasing = "purchasing"
}

export enum UnitType {
  Standard = "standard",
  Revised = "revised"
}

export enum DocumentType {
  WorkOrder = "work_order",
  ApprovedPlans = "approved_plans",
  ConstructionDrawing = "construction_drawing",
  BillOfMaterials = "bill_of_materials",
  Reference = "reference"
}

export enum TemplateStatus {
  Draft = "draft",
  ForApproval = "for_approval",
  Rejected = "rejected",
  Active = "active",
  Inactive = "inactive"
}

export enum TaxCategory {
  Vatable = "vatable",
  NonVatable = "non_vatable"
}

export enum PerformanceRateItemRatingType {
  TotalMaterialVarianceRate25 = "total_material_variance_rate_25",
  TotalLaborVarianceRate35 = "total_labor_variance_rate_35",
  ReworkOverbudgetRate20 = "rework_overbudget_rate_20",
  DurationVarianceRate20 = "duration_variance_rate_20"
}

export enum ProjectStatus {
  Draft = "draft",
  Pending = "pending",
  OnGoing = "on_going",
  Punchlisting = "punchlisting",
  Completed = "completed",
  Cancelled = "cancelled"
}

export enum ProjectType {
  House = "house",
  Additionals = "additionals",
  LandDevelopment = "land_development",
  OtherFixedAssets = "other_fixed_assets",
  SpecialProjects = "special_projects"
}

export enum FinanceType {
  Cash = "cash",
  InHouse = "in_house",
  Bank = "bank",
  PagIbig = "pag_ibig",
  "N/A" = "n_a"
}

export enum ProjectTaskItemType {
  Normal = "normal",
  RoofColor = "roof_color",
  WallColor = "wall_color",
  AccentColor = "accent_color",
  AccentTile = "accent_tile"
}

export enum MaterialRequestStatus {
  Open = "open",
  ForApproval = "for_approval",
  Rejected = "rejected",
  Closed = "closed",
  Expired = "expired"
}

export enum PurchaseRequestStatus {
  Open = "open",
  ForApproval = "for_approval",
  Rejected = "rejected",
  Closed = "closed",
  Draft = "draft",
  Cancelled = "cancelled"
}

export enum MaterialReturnStatus {
  Open = "open",
  Closed = "closed",
  Cancelled = "cancelled"
}

export enum MaterialReturnItemState {
  Good = "good",
  Scrap = "scrap"
}

export enum Action {
  Add = "add",
  Deduct = "deduct"
}

export enum PurchaseOrderStatus {
  Draft = "draft",
  Open = "open",
  Closed = "closed",
  Cancelled = "cancelled",
  PartiallyDelivered = "partially_delivered"
}

export enum CostLogStatus {
  Draft = "draft",
  ForApproval = "for_approval",
  Approved = "approved",
  Rejected = "rejected",
  Cancelled = "cancelled"
}

export enum CostLogSource {
  ProjectTask = "project_task",
  ChangeOrder = "change_order"
}

export enum ChangeOrderStatus {
  Draft = "draft",
  ForApproval = "for_approval",
  Rejected = "rejected",
  Active = "active",
  Inactive = "inactive"
}

export enum AccountEntryType {
  Debit = "debit",
  Credit = "credit"
}

export enum ChangeOrderType {
  Add = "add",
  Replace = "replace"
}

export const RoleSchema = yup.object({
  id: yup.number().nullable(true),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)")
    .required("Name is required."),
  department: yup.string().transform(nullToEmptyString).required("Department is required."),
  active: yup.boolean().required("Active status is required.")
});

export const UserSchema = yup.object({
  id: yup.number().nullable(true),
  first_name: yup
    .string()
    .transform(nullToEmptyString)
    .required("First name is required.")
    .min(2, "First name is too short (minimum is 2 characters)")
    .max(100, "First name is too long (maximum is 100 characters)"),
  last_name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Last name is required.")
    .min(2, "Last name is too short (minimum is 2 characters)")
    .max(100, "Last name is too long (maximum is 100 characters)"),
  email: yup.string().required("Email address is required").email("Invalid email address."),
  department: yup.string().transform(nullToEmptyString).required("Department is required."),
  role_id: yup.number().nullable().required("Role is required."),
  active: yup.boolean().required("Active status is required."),
  read_only: yup.boolean(),
  warehouse_id: yup
    .number()
    .nullable()
    .when("department", {
      is: (value: string) => {
        return value && value == Department.Logistics;
      },
      then: (rule) => {
        return rule.required("Designated warehouse is required.");
      }
    })
});

export const WarehouseSchema = yup.object({
  id: yup.number().nullable(true),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  active: yup.boolean().required("Active status is required."),
  account_id: yup.number().transform(emptyStringToNull).nullable().required("General Ledger Account is required.")
});

export const UnitMeasureSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Code is required.")
    .min(1, "Code is too short (minimum is 1 character)")
    .max(10, "Code is too long (maximum is 100 characters)"),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  active: yup.boolean().required("Active status is required.")
});

export const SubdivisionSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup.string().transform(nullToEmptyString).length(3, "Code should be 3 characters").required("Code is required."),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  address: yup
    .string()
    .transform(nullToEmptyString)
    .required("Address is required.")
    .min(2, "Address is too short (minimum is 2 characters)")
    .max(255, "Address is too long (maximum is 255 characters)"),
  active: yup.boolean().required("Active status is required."),
  phase_start: yup
    .number()
    .max(yup.ref("phase_end"), "Phase start must be less than phase end.")
    .transform(emptyStringToNull)
    .nullable()
    .required("Phase start is required."),
  phase_end: yup
    .number()
    .min(yup.ref("phase_start"), "Phase end must be greater than phase start.")
    .transform(emptyStringToNull)
    .nullable()
    .required("Phase end is required."),
  block_start: yup
    .number()
    .max(yup.ref("block_end"), "Block start must be less than block end.")
    .transform(emptyStringToNull)
    .nullable()
    .required("Block start is required."),
  block_end: yup
    .number()
    .min(yup.ref("block_start"), "Block end must be greater than block start.")
    .transform(emptyStringToNull)
    .nullable()
    .required("Block end is required."),
  lot_start: yup
    .number()
    .max(yup.ref("lot_end"), "Lot start must be less than lot end.")
    .transform(emptyStringToNull)
    .nullable()
    .required("Lot start is required."),
  lot_end: yup
    .number()
    .min(yup.ref("lot_start"), "Lot end must be greater than lot start.")
    .transform(emptyStringToNull)
    .nullable()
    .required("Lot end is required.")
});

export const UnitModelSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Code is required.")
    .min(2, "Code is too short (minimum is 2 characters)")
    .max(20, "Code is too long (maximum is 20 characters)"),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  active: yup.boolean().required("Active status is required.")
});

export const ItemCategorySchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Code is required.")
    .when("item_type", {
      is: "cost",
      then: yup.string().length(8, "Code must be 8 characters."),
      otherwise: yup.string().length(7, "Code must be 7 characters.")
    }),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  item_type: yup.string().transform(nullToEmptyString).required("Item type is required."),
  item_classification_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("item_type", {
      is: "inventory",
      then: yup.number().transform(emptyStringToNull).nullable().required("Item classification is required.")
    }),
  active: yup.boolean().required("Active status is required.")
});

export const ItemSubcategorySchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Code is required.")
    .when("item_type", {
      is: "cost",
      then: yup.string().length(8, "Code must be 8 characters."),
      otherwise: yup.string().length(7, "Code must be 7 characters.")
    }),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  item_category_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("item_type", {
      is: "inventory",
      then: yup.number().transform(emptyStringToNull).nullable().required("Inventory type is required."),
      otherwise: yup.number().transform(emptyStringToNull).nullable().required("Cost type is required.")
    }),
  active: yup.boolean().required("Active status is required.")
});

export const InventoryItemSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup.string().transform(nullToEmptyString).required("Code is required.").length(7, "Code must be 7 characters."),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  item_category_id: yup.number().transform(emptyStringToNull).nullable().required("Inventory Type is required."),
  item_subcategory_id: yup.number().transform(emptyStringToNull).nullable().required("Inventory Subtype is required."),
  unit_measure_id: yup.number().transform(emptyStringToNull).nullable().required("Unit of Measure is required."),
  reorder_point: yup
    .number()
    .transform(emptyStringToNull)
    .transform((value) => Number(value))
    .when("stockable", {
      is: (value) => value == true,
      then: yup.number().typeError("Reorder point is required.").min(1, "Minimum reorder point is 1."),
      otherwise: yup.number().nullable().notRequired()
    }),
  safety_stock_level: yup
    .number()
    .transform(emptyStringToNull)
    .transform((value) => Number(value))
    .when("stockable", {
      is: (value) => value == true,
      then: yup
        .number()
        .typeError("Safety stock level is required.")
        .required("Safety stock level is required.")
        .min(1, "Minimum safety stock level is 1."),
      otherwise: yup.number().nullable().notRequired()
    }),
  standard_price: yup
    .number()
    .transform(emptyStringToNull)
    .transform((value) => Number(value))
    .required("Standard price is required."),
  active: yup.boolean().required("Active status is required."),
  stockable: yup.boolean().required("Active status is required.")
});

export const CostItemSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup.string().transform(nullToEmptyString).required("Code is required.").length(8, "Code must be 8 characters."),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  item_category_id: yup.number().transform(emptyStringToNull).nullable().required("Inventory Type is required."),
  item_subcategory_id: yup.number().transform(emptyStringToNull).nullable().required("Inventory Subtype is required."),
  unit_measure_id: yup.number().transform(emptyStringToNull).nullable().required("Unit of Measure is required."),
  tax_category: yup.string().transform(nullToEmptyString).required("Tax Category is required."),
  withholding_tax_rate: yup
    .number()
    .transform(emptyStringToNull)
    .transform((value) => Number(value))
    .required("Withholding Tax Rate is required.")
    .oneOf([0.0, 0, 0.01, 0.02], "Withholding Tax Rate must be one of the values 0, 0.01, 0.02"),
  standard_price: yup
    .number()
    .transform(emptyStringToNull)
    .transform((value) => Number(value))
    .required("Standard price is required."),
  active: yup.boolean().required("Active status is required."),
  account_id: yup.number().transform(emptyStringToNull).nullable().required("General Ledger Account is required.")
});

export const CostLogItemSchema = yup.object().shape(
  {
    id: yup.number().transform(nanToNumber).nullable(),
    cost_log_id: yup.number().transform(nanToNumber).nullable(),
    task_id: yup.number().transform(emptyStringToNull).nullable().required("Task is required."),
    project_task_item_id: yup.number().transform(nanToNumber).nullable(),
    change_order_task_item_id: yup.number().transform(nanToNumber).nullable(),
    item_id: yup.number().transform(emptyStringToNull).nullable().required("Item is required."),
    unit_measure_id: yup.number().transform(emptyStringToNull).nullable(),
    budgeted_unit_rate: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("item_id", {
        is: (value) => value,
        then: (rule) => rule.required("This is required.")
      }),
    budgeted_quantity: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("item_id", {
        is: (value) => value,
        then: (rule) => rule.required("This is required.")
      }),
    used_quantity: yup.number().transform(emptyStringToNull).nullable().required("Quantity is required."),
    used_amount: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("used_quantity", {
        is: (value) => value && value > 0,
        then: (rule) => rule.required("This is required.")
      }),
    remarks: yup.string().transform(nullToEmptyString)
  },
  [["used_quantity", "used_amount"]]
);

export const CostLogSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  code: yup.string().transform(nullToEmptyString),
  project_id: yup.number().transform(emptyStringToNull).nullable().required("Project is required."),
  status: yup.mixed<CostLogStatus>().oneOf(Object.values(CostLogStatus)),
  source: yup.mixed<CostLogSource>().oneOf(Object.values(CostLogSource)),
  created_at: yup.date().nullable(),
  cost_log_items_attributes: yup.array(CostLogItemSchema).min(1, "Cost log must have at least one item.").required("required")
});

export const TaskClassificationSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Code is required.")
    .min(3, "Code is too short (minimum is 3 characters)")
    .max(30, "Code is too long (maximum is 30 characters)"),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  active: yup.boolean().required("Active status is required.")
});

export const TaskSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Code is required.")
    .min(3, "Code is too short (minimum is 3 characters)")
    .max(10, "Code is too long (maximum is 10 characters)"),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  item_type: yup.string().transform(nullToEmptyString).required("Item Type is required."),
  task_classification_id: yup.number().transform(emptyStringToNull).nullable().required("Task Classification is required."),
  item_subcategories: yup
    .array(yup.number())
    .transform(nullToEmptyArray)
    .required("Item Subtypes is required.")
    .min(1, "Please select at least one Item Subtype."),
  active: yup.boolean().required("Active status is required.")
});

export const TemplateTaskItemSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  template_task_id: yup.number().transform(nanToNumber).nullable(),
  item_id: yup.number().transform(emptyStringToNull).nullable().required("Item is required."),
  budgeted_unit_rate: yup.number().transform(emptyStringToNull).nullable().required("Budgeted Unit Rate is required."),
  budgeted_quantity: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .typeError("Budgeted Quantity is invalid.")
    .required("Budgeted Quantity is required.")
    .test({
      name: "budgeted_quantity",
      exclusive: false,
      message: "Budgeted Quantity is invalid.",
      test: function () {
        return +this.originalValue > 0;
      }
    }),
  unit_measure_id: yup.number().transform(emptyStringToNull).nullable().required("Unit measure is required.")
});

export const TemplateTaskSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  template_id: yup.number().transform(nanToNumber).nullable(),
  task_id: yup.number().transform(emptyStringToNull).nullable().required("Task is required."),
  weight: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .typeError("Weight is invalid.")
    .required("Weight is required.")
    .test({
      name: "weight",
      exclusive: false,
      message: "Weight is invalid.",
      test: function () {
        return +this.originalValue >= 0;
      }
    }),
  prerequisite_task_id: yup.number().transform(emptyStringToNull).nullable(),
  template_task_items_attributes: yup.array(TemplateTaskItemSchema).min(1, "Template task must have at least one item.").required("required")
});

export const DocumentSchema = yup.object({
  document_type: yup.string().transform(nullToEmptyString).oneOf(Object.values(DocumentType)),
  document_data_uri: yup.string().transform(nullToEmptyString),
  file_name: yup.string().transform(nullToEmptyString),
  file_url: yup.string().transform(nullToEmptyString),
  description: yup
    .string()
    .notRequired()
    .transform(nullToEmptyString)
    .test("length", "Description is too short (minimum is 2 characters)", function (value) {
      if (value?.length == 0) {
        return true;
      }
      return value && value.length >= 2;
    })
    .test("length", "Description is too long (maximum is 255 characters)", function (value) {
      if (value?.length == 0) {
        return true;
      }
      return value && value.length <= 255;
    }),
  remarks: yup
    .string()
    .notRequired()
    .transform(nullToEmptyString)
    .test("length", "Remarks is too short (minimum is 2 characters)", function (value) {
      if (value?.length == 0) {
        return true;
      }
      return value && value.length >= 2;
    })
    .test("length", "Remarks is too long (maximum is 255 characters)", function (value) {
      if (value?.length == 0) {
        return true;
      }
      return value && value.length <= 255;
    })
});

export const TemplateSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Template ID is required.")
    .when("unit_type", {
      is: UnitType.Standard,
      then: yup.string().matches(StandardTemplateIdRegex, "Format is: STD-SUBDIVISIONPHASEMODEL-VERSION"),
      otherwise: yup.string().matches(RevisedTemplateIdRegex, "Format is: REV-SUBDIVISIONPHASEMODEL-BLOCKLOT")
    }),
  description: yup
    .string()
    .transform(nullToEmptyString)
    .nullable()
    .when("status", {
      is: (value: TemplateStatus) => {
        return value && value != TemplateStatus.Draft;
      },
      then: (rule) => {
        return rule.required("Description is required.");
      }
    }),
  technology: yup
    .string()
    .transform(nullToEmptyString)
    .nullable()
    .when("status", {
      is: (value: TemplateStatus) => {
        return value && value != TemplateStatus.Draft;
      },
      then: (rule) => {
        return rule
          .required("Technology is required.")
          .min(3, "Technology is too short (minimum is 3 characters)")
          .max(100, "Technology is too long (maximum is 100 characters)");
      }
    }),
  days_to_complete: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: TemplateStatus) => {
        return value && value != TemplateStatus.Draft;
      },
      then: (rule) => {
        return rule
          .required("Days to Complete is required.")
          .integer("Days to Complete must be an integer.")
          .positive("Days to Complete must be a positive integer.");
      }
    }),
  subdivision_id: yup.number().transform(emptyStringToNull).nullable().required("Subdivision is required."),
  unit_type: yup.string().transform(nullToEmptyString).required("Unit Type is required."),
  unit_model_id: yup.number().transform(emptyStringToNull).nullable().required("Unit Model is required."),
  phase: yup.string().transform(nullToEmptyString).required("Phase is required."),
  block: yup
    .string()
    .transform(nullToEmptyString)
    .when("unit_type", {
      is: UnitType.Revised,
      then: yup.string().transform(nullToEmptyString).required("Block is required.")
    }),
  lot: yup
    .string()
    .transform(nullToEmptyString)
    .when("unit_type", {
      is: UnitType.Revised,
      then: yup.string().transform(nullToEmptyString).required("Lot is required.")
    }),
  status: yup.mixed<TemplateStatus>().oneOf(Object.values(TemplateStatus)),
  documents_attributes: yup
    .array()
    .of(DocumentSchema)
    .when("status", {
      is: (value: TemplateStatus) => {
        return value && value == TemplateStatus.Draft;
      },
      otherwise: (rule) => {
        return rule.test("construction_drawing", "required.", (documents) => {
          return documents.some((document) => document.document_type == DocumentType.ConstructionDrawing && document.file_name);
        });
      }
    }),
  template_tasks_attributes: yup.array(TemplateTaskSchema).when("status", {
    is: (value: TemplateStatus) => {
      return value && value == TemplateStatus.Draft;
    },
    otherwise: (rule) => {
      return rule
        .min(1, "Template must have at least one task")
        .required("required")
        .test("sum", "The total task weight must be 100.", (template_tasks = []) => {
          const totalWeight = template_tasks.reduce((total, template_task) => {
            return total + (template_task.weight || 0);
          }, 0);

          return totalWeight == 100;
        });
    }
  })
});

export const AccountSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup.string().transform(nullToEmptyString).required("Code is required.").length(17, "Code must be 17 characters."),
  name: yup
    .string()
    .transform(nullToEmptyString)
    .required("Name is required.")
    .min(2, "Name is too short (minimum is 2 characters)")
    .max(100, "Name is too long (maximum is 100 characters)"),
  active: yup.boolean().required("Active status is required."),
  account_subgroup_id: yup.number().transform(emptyStringToNull).nullable().required("GL Account Subgroup is required."),
  account_entry_type: yup.string().transform(nullToEmptyString).oneOf(Object.values(AccountEntryType))
});

export const AllocationRuleSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup.string().transform(nullToEmptyString).required("Code is required."),
  name: yup.string().transform(nullToEmptyString).required("Name is required."),
  active: yup.boolean().required("Active status is required."),
  account_id: yup.number().transform(emptyStringToNull).nullable().required("General Ledger Account is required.")
});

export const SupplierSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup.string().transform(nullToEmptyString).required("Code is required."),
  name: yup.string().transform(nullToEmptyString).required("Name is required."),
  active: yup.boolean().required("Active status is required."),
  tax_number: yup.string().transform(nullToEmptyString).required("Tax Identification Number is required."),
  tax_category: yup.string().transform(nullToEmptyString).oneOf(Object.values(TaxCategory)),
  withholding_tax_rate: yup
    .number()
    .typeError("Withholding Tax Rate is required.")
    .oneOf([0.0, 0, 0.01, 0.02], "Withholding Tax Rate must be one of the values 0, 0.01, 0.02"),
  address: yup.string().transform(nullToEmptyString).required("Address is required."),
  email: yup.string().transform(nullToEmptyString).email("Invalid email address."),
  contact_number: yup.string().transform(nullToEmptyString),
  remarks: yup.string().transform(nullToEmptyString),
  account_id: yup.number().transform(emptyStringToNull).nullable().required("General Ledger Account is required.")
});

export const PerformanceRateItemSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  rating_type: yup.mixed<PerformanceRateItemRatingType>().oneOf(Object.values(PerformanceRateItemRatingType)).required("Rating type is required."),
  min: yup.number().transform(emptyStringToNull).nullable().required("Min is required."),
  max: yup.number().transform(emptyStringToNull).nullable().required("Max is required."),
  rating: yup.number().transform(emptyStringToNull).nullable().required("Rating is required.")
});

export const PerformanceRateSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  name: yup.string().transform(nullToEmptyString).required("Performance Rate Name is required."),
  performance_rate_items_attributes: yup.array(PerformanceRateItemSchema).min(1, "Performance Rate must have at least one item.").required("required")
});

export const ParticularSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  project_id: yup.number().transform(nanToNumber).nullable(),
  item_id: yup.number().transform(emptyStringToNull).nullable().required("Item is required."),
  budgeted_unit_rate: yup.number().transform(emptyStringToNull).nullable().required("Budgeted Unit Rate is required."),
  budgeted_quantity: yup.number().transform(emptyStringToNull).nullable().required("Budgeted Quantity is required."),
  unit_measure_id: yup.number().transform(emptyStringToNull).nullable().required("Unit measure is required.")
});

export const ProjectTaskItemSchema = yup.object({
  project_task_id: yup.number().transform(nanToNumber).nullable(),
  item_id: yup.number().transform(emptyStringToNull).nullable().required("Item is required."),
  budgeted_unit_rate: yup.number().transform(emptyStringToNull).nullable().required("Budgeted Unit Rate is required."),
  budgeted_quantity: yup.number().transform(emptyStringToNull).nullable().required("Budgeted Quantity is required."),
  unit_measure_id: yup.number().transform(emptyStringToNull).nullable().required("Unit measure is required."),
  variant: yup.string().transform(nullToEmptyString).oneOf(Object.values(ProjectTaskItemType)).required("Project task item variant is required.")
});

export const ProjectTaskSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  project_id: yup.number().transform(nanToNumber).nullable(),
  task_id: yup.number().transform(emptyStringToNull).nullable().required("Task is required."),
  weight: yup.number().transform(emptyStringToNull).nullable().required("Weight is required."),
  prerequisite_task_id: yup.number().transform(emptyStringToNull).nullable(),
  project_task_items_attributes: yup.array(ProjectTaskItemSchema).min(1, "Particulars must have at least 4 items.").required("required")
});

export const ProjectSchema = yup.object({
  id: yup.number().nullable(true),
  code: yup
    .string()
    .transform(nullToEmptyString)
    .required("Project ID is required.")
    .min(7, "Project ID is too short (minimum is 7 characters)")
    .max(20, "Project ID is too long (maximum is 20 characters)"),
  name: yup.string().transform(nullToEmptyString).required("Project Name is required."),
  template_id: yup.number().transform(emptyStringToNull).nullable().required("Template is required."),
  phase: yup.string().transform(nullToEmptyString).required("Phase is required."),
  block: yup.string().transform(nullToEmptyString).required("Block is required."),
  lot: yup.string().transform(nullToEmptyString).required("Lot is required."),
  subdivision_id: yup.string().transform(nullToEmptyString).required("Subdivision is required."),
  unit_model_id: yup.string().transform(nullToEmptyString).required("Unit model is required."),
  project_type: yup.string().transform(nullToEmptyString).oneOf(Object.values(ProjectType)).required("Project type is required."),
  unit_type: yup.string().transform(nullToEmptyString).oneOf(Object.values(UnitType)).required("Project unit is required."),
  customer_first_name: yup.string().transform(nullToEmptyString).required("Customer first name is required."),
  customer_last_name: yup.string().transform(nullToEmptyString).required("Customer last name is required."),
  finance_type: yup.string().nullable().optional().transform(nullToEmptyString).oneOf(Object.values(FinanceType)),
  equity: yup.number().transform(emptyStringToNull).nullable().optional(),
  manager_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.required("Project manager is required.");
      }
    }),
  architect_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.required("Architect is required.");
      }
    }),
  engineer_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft && value != ProjectStatus.Pending;
      },
      then: (rule) => {
        return rule.required("Engineer is required.");
      }
    }),
  foreman_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft && value != ProjectStatus.Pending;
      },
      then: (rule) => {
        return rule.required("Foreman is required.");
      }
    }),
  warehouse_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.required("Designated warehouse is required.");
      }
    }),
  allocation_rule_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.required("Allocation rule is required.");
      }
    }),
  authorized_by_design_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.required("This is required.");
      }
    }),
  authorized_by_sales_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.required("This is required.");
      }
    }),
  authorized_by_finance_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.required("This is required.");
      }
    }),
  remarks: yup.string().transform(nullToEmptyString),
  include_to_allocations: yup.boolean().default(false),
  status: yup.mixed<ProjectStatus>().oneOf(Object.values(ProjectStatus)).required("Status is required."),
  documents_attributes: yup
    .array()
    .of(DocumentSchema)
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value != ProjectStatus.Draft;
      },
      then: (rule) => {
        return rule.test("documents", "required", (documents) => {
          return documents?.filter((document) => document.file_name).length == documents?.length;
        });
      }
    }),
  project_tasks_attributes: yup.array().optional()
});

export const ItemSupplierSchema = yup.object().shape(
  {
    item_id: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("supplier_id", {
        is: null || undefined,
        then: (rule) => {
          return rule.required("Inventory item is required.");
        }
      }),
    supplier_id: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("item_id", {
        is: null || undefined,
        then: (rule) => {
          return rule.required("Supplier is required.");
        }
      }),
    min_order_quantity: yup.number().transform(emptyStringToNull).nullable().required("Minimum order quantity is required."),
    lead_time: yup.number().transform(emptyStringToNull).nullable().required("Lead time is required."),
    current_price: yup.number().transform(emptyStringToNull).nullable().required("Current price is required."),
    credit_term_id: yup.number().transform(emptyStringToNull).nullable().required("Credit term is required."),
    preferred: yup.boolean()
  },
  [["item_id", "supplier_id"]]
);

export const ProjectRequisitionSchema = yup.object().shape(
  {
    task_id: yup.number().transform(emptyStringToNull).nullable(),
    item_id: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("task_id", {
        is: (value) => value,
        then: (rule) => rule.required("Item is required.")
      }),
    budgeted_quantity: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("task_id", {
        is: (value) => value,
        then: (rule) => rule.required("This is required.")
      }),
    requested_quantity: yup
      .number()
      .transform(emptyStringToNull)
      .nullable()
      .when("date_needed", {
        is: (date_needed) => date_needed,
        then: (rule) => {
          return rule.required("Requested quantity is required.").test({
            name: "requested_quantity",
            exclusive: false,
            message: "Requested Quantity is invalid.",
            test: function (value, context) {
              if (context.parent.inventory_quantity == 0) {
                return true;
              } else {
                if (value && value > context.parent.inventory_quantity) {
                  return context.createError({ message: `Maximum is ${context.parent.inventory_quantity}` });
                } else {
                  return true;
                }
              }
            }
          });
        }
      }),
    date_needed: yup
      .date()
      .nullable()
      .when("requested_quantity", {
        is: (requested_quantity) => {
          return requested_quantity && requested_quantity > 0;
        },
        then: (rule) => {
          return rule.required("Date needed is required.");
        }
      }),
    remarks: yup.string().notRequired()
  },
  [["requested_quantity", "date_needed"]]
);

export const ProjectRequisitionsSchema = yup.object({
  data: yup.array(ProjectRequisitionSchema)
});

export const ItemInventorySchema = yup.object({
  inventory_item_id: yup.number().typeError("Inventory item is required.").required("Inventory item is required."),
  action: yup.string().transform(nullToEmptyString).oneOf(Object.values(Action)).required("Action is required."),
  adjustment_reason_id: yup.number().transform(emptyStringToNull).required("Adjustment reason is required."),
  warehouses: yup.array(yup.number()).transform(nullToEmptyArray).required("Warehouse is required.").min(1, "Warehouse is required."),
  quantity: yup.number().typeError("Quantity is error required.").required("Quantity is required."),
  adjustment: yup
    .number()
    .typeError("Adjustment is required.")
    .required("Adjustment is required.")
    .test({
      name: "adjustment",
      exclusive: false,
      message: "Invalid adjustment.",
      test: function () {
        return parseFloat(this.parent.quantity) >= 0;
      }
    })
});

export const DeliveryItemSchema = yup.object({
  ordered_quantity: yup.number().typeError("Ordered quantity is required.").required("Ordered quantity is required."),
  delivered_quantity: yup
    .number()
    .typeError("Delivered quantity is required.")
    .required("Delivered quantity is required.")
    .when("allow_partial_delivery", {
      is: true,
      then: (rule) => {
        return rule.test("delivered_quantity", "Delivered quantity must be 0 or ordered_quantity.", function (value, schema) {
          if (value === undefined) return false;
          return value >= 0 && value <= schema.parent.ordered_quantity;
        });
      },
      otherwise: (rule) => {
        return rule.test("delivered_quantity", "Delivered and ordered quantity do not match.", function (value, schema) {
          if (value === undefined) return false;
          return value === schema.parent.ordered_quantity;
        });
      }
    })
});

export const DeliverySchema = yup.object({
  purchase_order_id: yup.number().typeError("Purchase order is required.").required("Purchase order is required."),
  delivered_by: yup.string().required("Delivered by is required."),
  received_by: yup.string().required("Received by is required."),
  remarks: yup.string().transform(nullToEmptyString).notRequired(),
  allow_partial_delivery: yup.boolean().required(),
  delivery_items: yup.array(DeliveryItemSchema).test("sum", "Delivery needs to receive at least one material.", (deliveries = []) => {
    const totalDeliveredQuantity = deliveries.reduce((total, delivery) => {
      return total + (delivery.delivered_quantity || 0);
    }, 0);

    return totalDeliveredQuantity > 0;
  })
});

export const PurchaseOrderItemSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  item_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("id", {
      is: null || undefined,
      then: (rule) => rule.required("Item is required.")
    }),
  quantity: yup.number().typeError("Order quantity is required.").required("Order quantity is required."),
  remarks: yup.string().transform(nullToEmptyString)
});

export const PurchaseOrderSchema = yup.object({
  id: yup.number().typeError("Purchase order is required.").required("Purchase order is required."),
  status: yup.string().transform(nullToEmptyString).oneOf(Object.values(PurchaseOrderStatus)).required("Purchase order status is required."),
  allow_partial_delivery: yup.boolean().required("Allow partial delivery is required."),
  expected_delivery_date: yup
    .string()
    .nullable()
    .when("status", {
      is: (value: PurchaseOrderStatus) => value != PurchaseOrderStatus.Draft,
      then: (rule) => {
        return rule.typeError("Expected delivery date is required.").required("Expected delivery date is required.");
      }
    }),
  purchase_order_items: yup
    .array(PurchaseOrderItemSchema)
    .min(1, "Purchase order needs at least one item inventory.")
    .test("one", "Purchase order needs at least one item inventory.", (purchase_order_items) => {
      return purchase_order_items.some((purchase_order_item) => purchase_order_item.quantity);
    })
});

export const MaterialReorderingSchema = yup.object({
  warehouse_id: yup.number().typeError("Warehouse is required.").required("Warehouse is required."),
  material_reordering: yup
    .array()
    .min(1, "Replenishment needs at least one item inventory.")
    .test("one", "Material replenishment request needs at least one item inventory.", (material_reorderings) => {
      return material_reorderings.some((material_reordering) => material_reordering.selected);
    })
});

export const ChangePasswordSchema = yup.object({
  id: yup.number().typeError("User id is required.").required("User id is required."),
  password: yup
    .string()
    .transform(nullToEmptyString)
    .required("Password is required.")
    .min(8, "Minimum characters is 8.")
    .max(20, "Maximum characters is 20."),
  confirm_password: yup
    .string()
    .transform(nullToEmptyString)
    .required("Password confirmation is required.")
    .min(8, "Minimum characters is 8.")
    .max(20, "Maximum characters is 20.")
    .oneOf([yup.ref("password"), null], "Passwords do not match.")
});

export const ChangeOrderTaskItemSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  change_order_task_id: yup.number().transform(nanToNumber).nullable(),
  item_id: yup.number().transform(emptyStringToNull).nullable().required("Item is required."),
  budgeted_unit_rate: yup.number().transform(emptyStringToNull).nullable().required("Budgeted Unit Rate is required."),
  budgeted_quantity: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("type", {
      is: (value) => {
        return value && value == "replace";
      },
      then: (rule) => {
        return rule.test({
          name: "budgeted_quantity",
          exclusive: false,
          message: "Budgeted Quantity is invalid.",
          test: function (value, context) {
            if (context.parent.actual_quantity == 0) {
              return true;
            } else {
              if (
                value == null ||
                value == undefined ||
                (value && value < context.parent.actual_quantity && context.parent.can_change_budgeted_quantity)
              ) {
                return context.createError({ message: `Minimum is ${context.parent.actual_quantity}` });
              } else {
                return true;
              }
            }
          }
        });
      },
      otherwise: (rule) => {
        return rule.test({
          name: "budgeted_quantity",
          exclusive: false,
          message: "Budgeted Quantity is invalid.",
          test: function (value) {
            if (value && value > 0) return true;
            else return false;
          }
        });
      }
    }),
  type: yup.string().nullable(),
  unit_measure_id: yup.number().transform(emptyStringToNull).nullable().required("Unit measure is required.")
});

export const ChangeOrderTaskSchema = yup.object({
  id: yup.number().transform(nanToNumber).nullable(),
  change_order_id: yup.number().transform(nanToNumber).nullable(),
  task_id: yup.number().transform(emptyStringToNull).nullable().required("Task is required."),
  replacement_task_id: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .when("type", {
      is: (value) => {
        return value && value == "replace";
      },
      then: (rule) => {
        return rule.required("Replacement task is required.");
      }
    }),
  type: yup.string().nullable(),
  change_order_task_items_attributes: yup
    .array(ChangeOrderTaskItemSchema)
    .min(1, "Change order task must have at least one item.")
    .required("required")
});

export const ChangeOrderSchema = yup.object({
  id: yup.number().nullable(true),
  change_order_type: yup.mixed<ChangeOrderType>().oneOf(Object.values(ChangeOrderType)),
  code: yup.string().transform(nullToEmptyString),
  description: yup.string().transform(nullToEmptyString).nullable(true),
  include_budget: yup.boolean().required("Include budget is required."),
  documents_attributes: yup
    .array()
    .of(DocumentSchema)
    .when("status", {
      is: (value: ProjectStatus) => {
        return value && value == ProjectStatus.Draft;
      },
      otherwise: (rule) => {
        return rule.test("reference", "required.", (documents) => {
          return documents.some((document) => document.document_type == DocumentType.Reference && document.file_name);
        });
      }
    }),
  change_order_tasks_attributes: yup.array(ChangeOrderTaskSchema).when("status", {
    is: (value: ProjectStatus) => {
      return value && value == ProjectStatus.Draft;
    },
    otherwise: (rule) => {
      return rule.min(1, "Change order must have at least one task").required("required");
    }
  })
});

export const ReturnSchema = yup.object({
  item_id: yup.number().required("Item is required"),
  state: yup
    .string()
    .transform(nullToEmptyString)
    .nullable()
    .when("returned_quantity", {
      is: (value) => value && value > 0,
      then: (rule) => rule.oneOf(Object.values(MaterialReturnItemState)).required("State is required.")
    }),
  remarks: yup.string().notRequired()
});

export const ReturnsSchema = yup.object({
  project_id: yup.number().required("Project is required"),
  returns: yup.array(ReturnSchema).test("sum", "Inventory return needs to return at least one material.", (inventoryReturns = []) => {
    const totalDeliveredQuantity = inventoryReturns.reduce((total, inventoryReturn) => {
      return total + (parseFloat(inventoryReturn.returned_quantity) || 0);
    }, 0);

    return totalDeliveredQuantity > 0;
  })
});

export const UpdatePriceSchema = yup.object({
  new_price: yup.number().typeError("New price is required.").required("New price is required."),
  perpetual: yup.boolean().nullable(),
  end_date: yup
    .date()
    .nullable()
    .when("perpetual", {
      is: (perpetual) => {
        return !perpetual;
      },
      then: (rule) => {
        return rule.required("End date is required.");
      }
    })
});

export type RoleFormData = yup.InferType<typeof RoleSchema>;
export type UserFormData = yup.InferType<typeof UserSchema>;
export type WarehouseFormData = yup.InferType<typeof WarehouseSchema>;
export type UnitMeasureFormData = yup.InferType<typeof UnitMeasureSchema>;
export type SubdivisionFormData = yup.InferType<typeof SubdivisionSchema>;
export type UnitModelFormData = yup.InferType<typeof UnitModelSchema>;
export type ItemCategoryFormData = yup.InferType<typeof ItemCategorySchema>;
export type ItemSubcategoryFormData = yup.InferType<typeof ItemSubcategorySchema>;
export type InventoryItemFormData = yup.InferType<typeof InventoryItemSchema>;
export type CostItemFormData = yup.InferType<typeof CostItemSchema>;
export type CostLogFormData = yup.InferType<typeof CostLogSchema>;
export type CostLogItemFormData = yup.InferType<typeof CostLogItemSchema>;
export type TaskClassificationFormData = yup.InferType<typeof TaskClassificationSchema>;
export type TaskFormData = yup.InferType<typeof TaskSchema>;
export type TemplateFormData = yup.InferType<typeof TemplateSchema>;
export type TemplateTaskFormData = yup.InferType<typeof TemplateTaskSchema>;
export type TemplateTaskItemFormData = yup.InferType<typeof TemplateTaskItemSchema>;
export type DocumentFormData = yup.InferType<typeof DocumentSchema>;
export type AccountFormData = yup.InferType<typeof AccountSchema>;
export type AllocationRuleFormData = yup.InferType<typeof AllocationRuleSchema>;
export type PerformanceRateFormData = yup.InferType<typeof PerformanceRateSchema>;
export type ProjectFormData = yup.InferType<typeof ProjectSchema>;
export type SupplierFormData = yup.InferType<typeof SupplierSchema>;
export type ItemSupplierFormData = yup.InferType<typeof ItemSupplierSchema>;
export type ProjectRequisitionFormData = yup.InferType<typeof ProjectRequisitionSchema>;
export type ProjectRequisitionsFormData = yup.InferType<typeof ProjectRequisitionsSchema>;
export type DeliveryFormData = yup.InferType<typeof DeliverySchema>;
export type ChangePasswordFormData = yup.InferType<typeof ChangePasswordSchema>;
export type PurchaseOrderItemFormData = yup.InferType<typeof PurchaseOrderItemSchema>;
